/**
 * surface-protocol — the ONE guest-surface contract (Surface Protocol law,
 * `foundry/ONE-SUBSTRATE.md`). A portal panel (host) frames a games-served scene
 * (guest) in an iframe: the guest declares its capabilities, the host drives
 * typed commands. This is the first implementation of the contract; new seams get
 * it, old dialects (the `popin:theme-preview` channel — see ../preview) migrate
 * opportunistically and are NOT rewritten here.
 *
 * Shape: **MCP-Apps-shaped** — an MCP message is JSON-RPC 2.0, so the wire format
 * IS JSON-RPC 2.0 (`{jsonrpc:"2.0", id, method, params}`), namespaced under
 * `surface/*`. No bespoke dialect is invented.
 *
 * This module is PURE — no React, no DOM, no `window`. It owns the envelope
 * types, builders, parser/guards, the capability gate, and the host lifecycle
 * reducer. The React frame (./scene-frame) is a thin shell that wires this to a
 * real iframe + postMessage + a timer; the command seam (./scene-host) adds the
 * one-analytics-event-per-command instrumentation. Keeping the lifecycle here is
 * what makes "boots, handshakes, degrades to placard" provable headless (the same
 * pure-core + DOM-shell split ../preview/preview-channel uses).
 *
 * Learnings reused (NOT re-implemented):
 *   • penpal / FramedSurface — guest speaks first (its listener must be attached
 *     before the host posts, or the message is dropped); id-correlated request →
 *     result; capability handshake before driving.
 *   • BrandPreviewFrame — pin the target origin (never `*`), accept messages only
 *     from the framed window at the expected origin.
 */

/** Bump when the envelope or handshake changes shape (semver-ish date tag). */
export const SURFACE_PROTOCOL_VERSION = "surface/2026-07-06";

export const JSONRPC_VERSION = "2.0";

/** Reserved method names (all `surface/*`). Commands the host drives at the guest
 *  are namespaced `scene/*` and gated by the declared capabilities. */
export const HELLO_METHOD = "surface/hello";

/** JSON-RPC error codes we use (host↔guest). -32000..-32099 is the impl-defined
 *  server-error range per the JSON-RPC 2.0 spec; the rest mirror the spec. */
export const SURFACE_ERROR = {
  /** Host attempted a command the guest never declared (capability gate). */
  METHOD_NOT_SUPPORTED: -32601,
  /** Malformed / non-conforming envelope. */
  INVALID_ENVELOPE: -32600,
  /** Guest-side failure executing a command. */
  COMMAND_FAILED: -32000,
} as const;

// ── envelope types ───────────────────────────────────────────────────────────

/** A JSON-RPC id — string in our usage (we mint `cmd_<n>`), but numbers are valid. */
export type SurfaceId = string | number;

/** Host→guest command: a JSON-RPC request (always carries an `id` for correlation). */
export interface SurfaceCommand<P = unknown> {
  jsonrpc: typeof JSONRPC_VERSION;
  id: SurfaceId;
  method: string;
  params?: P;
}

/** Guest→host notification: a JSON-RPC notification (NO `id` — fire-and-forget). */
export interface SurfaceNotification<P = unknown> {
  jsonrpc: typeof JSONRPC_VERSION;
  method: string;
  params?: P;
}

/** Guest→host reply to a command (success). */
export interface SurfaceResult<R = unknown> {
  jsonrpc: typeof JSONRPC_VERSION;
  id: SurfaceId;
  result: R;
}

/** Guest→host reply to a command (failure). */
export interface SurfaceError {
  jsonrpc: typeof JSONRPC_VERSION;
  id: SurfaceId;
  error: { code: number; message: string; data?: unknown };
}

export type SurfaceEnvelope =
  | SurfaceCommand
  | SurfaceNotification
  | SurfaceResult
  | SurfaceError;

// ── capability declaration (the `surface/hello` handshake) ────────────────────

/** Who the guest is + what it can do. Declared once, unprompted, on load. */
export interface SurfaceCapabilities {
  /** Protocol version the guest speaks (host checks compatibility by major tag). */
  protocolVersion: string;
  /** `scene/*` command methods the guest handles. The host MUST NOT drive a
   *  command absent from this list (capability gate → METHOD_NOT_SUPPORTED). */
  commands: string[];
}

/** Identity of the framed scene (for placards, analytics, logging). */
export interface SceneIdentity {
  id: string;
  name: string;
}

/** Params of the `surface/hello` notification the guest posts on load. */
export interface HelloParams {
  capabilities: SurfaceCapabilities;
  scene: SceneIdentity;
}

// ── builders ──────────────────────────────────────────────────────────────────

/** Build the guest's `surface/hello` notification (used by the reference guest
 *  and unit tests; the real guest lives in games-served scene code). */
export function helloNotification(params: HelloParams): SurfaceNotification<HelloParams> {
  return { jsonrpc: JSONRPC_VERSION, method: HELLO_METHOD, params };
}

/** Build a host→guest command envelope. `id` correlates the guest's reply. */
export function command<P>(id: SurfaceId, method: string, params?: P): SurfaceCommand<P> {
  const env: SurfaceCommand<P> = { jsonrpc: JSONRPC_VERSION, id, method };
  if (params !== undefined) env.params = params;
  return env;
}

/** Build a guest→host success reply. */
export function result<R>(id: SurfaceId, value: R): SurfaceResult<R> {
  return { jsonrpc: JSONRPC_VERSION, id, result: value };
}

/** Build a guest→host error reply. */
export function errorResult(
  id: SurfaceId,
  code: number,
  message: string,
  data?: unknown,
): SurfaceError {
  return { jsonrpc: JSONRPC_VERSION, id, error: data === undefined ? { code, message } : { code, message, data } };
}

// ── parser + guards ───────────────────────────────────────────────────────────

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/**
 * Parse untrusted postMessage data into a typed envelope, or null if it is not a
 * conforming JSON-RPC 2.0 surface message. The caller is responsible for the
 * origin/source checks BEFORE calling this (see BrandPreviewFrame's discipline);
 * this only validates shape. Never throws.
 */
export function parseEnvelope(data: unknown): SurfaceEnvelope | null {
  if (!isRecord(data) || data.jsonrpc !== JSONRPC_VERSION) return null;

  const hasId = "id" in data && (typeof data.id === "string" || typeof data.id === "number");
  const hasMethod = typeof data.method === "string";

  // result / error replies carry an id and one of result|error.
  if (hasId && "result" in data) {
    return { jsonrpc: JSONRPC_VERSION, id: data.id as SurfaceId, result: data.result };
  }
  if (hasId && "error" in data && isRecord(data.error) && typeof data.error.code === "number") {
    const e = data.error as { code: number; message?: unknown; data?: unknown };
    return errorResult(data.id as SurfaceId, e.code, typeof e.message === "string" ? e.message : "", e.data);
  }
  // request (has id + method) vs notification (method, no id).
  if (hasMethod && hasId) {
    return command(data.id as SurfaceId, data.method as string, data.params);
  }
  if (hasMethod) {
    return { jsonrpc: JSONRPC_VERSION, method: data.method as string, params: data.params };
  }
  return null;
}

export function isNotification(e: SurfaceEnvelope): e is SurfaceNotification {
  return "method" in e && !("id" in e);
}

export function isCommand(e: SurfaceEnvelope): e is SurfaceCommand {
  return "method" in e && "id" in e;
}

export function isResult(e: SurfaceEnvelope): e is SurfaceResult {
  return "result" in e;
}

export function isError(e: SurfaceEnvelope): e is SurfaceError {
  return "error" in e;
}

/** True when the envelope is the guest's `surface/hello`, with well-formed params. */
export function isHello(e: SurfaceEnvelope): e is SurfaceNotification<HelloParams> {
  if (!isNotification(e) || e.method !== HELLO_METHOD) return false;
  const p = e.params;
  return (
    isRecord(p) &&
    isRecord(p.capabilities) &&
    Array.isArray(p.capabilities.commands) &&
    isRecord(p.scene)
  );
}

// ── capability gate ───────────────────────────────────────────────────────────

/** Whether the guest declared support for a `scene/*` command. The host uses this
 *  to refuse driving a verb the guest can't handle (rather than posting into the
 *  void). */
export function supportsCommand(caps: SurfaceCapabilities | null, method: string): boolean {
  return !!caps && caps.commands.includes(method);
}

/** Major-version compatibility: the date tag before the last path segment. Two
 *  guests are compatible with this host when their protocol major matches ours.
 *  (v0: exact-string, but funnelled through one function so the policy can loosen
 *  without touching call sites.) */
export function isProtocolCompatible(guestVersion: string): boolean {
  return guestVersion === SURFACE_PROTOCOL_VERSION;
}

// ── host lifecycle reducer (boots → handshakes → live | offline) ──────────────

export type HostPhase =
  /** Frame mounted; waiting for the guest's `surface/hello`. */
  | "connecting"
  /** Guest handshook; host may drive commands. */
  | "live"
  /** Guest never handshook (server down / unreachable) → honest placard. */
  | "offline";

export interface HostState {
  phase: HostPhase;
  /** The scene URL being framed — named in the offline placard (never hidden). */
  url: string;
  capabilities: SurfaceCapabilities | null;
  scene: SceneIdentity | null;
  /** Why we're offline / degraded, if applicable (shown in the placard). */
  reason: string | null;
  /** Bumped on each (re)mount so the frame can key its iframe + timer. */
  attempt: number;
}

export type HostEvent =
  /** (Re)mount / reload the frame at `url`. */
  | { type: "mount"; url: string }
  /** Guest's `surface/hello` arrived (origin-verified by the caller). */
  | { type: "hello"; capabilities: SurfaceCapabilities; scene: SceneIdentity }
  /** No `surface/hello` within the handshake window → degrade. */
  | { type: "timeout" }
  /** Transport/guest error (iframe failed to load, incompatible protocol, …). */
  | { type: "error"; reason: string };

export function initialHostState(url: string): HostState {
  return { phase: "connecting", url, capabilities: null, scene: null, reason: null, attempt: 0 };
}

/**
 * The pure host lifecycle. Every transition the frame can make lives here, so the
 * headless proof can drive boots → handshake → live and boots → timeout → offline
 * with zero DOM.
 */
export function hostReducer(state: HostState, event: HostEvent): HostState {
  switch (event.type) {
    case "mount":
      // Fresh attempt: back to connecting, capabilities cleared, timer restarts.
      return {
        phase: "connecting",
        url: event.url,
        capabilities: null,
        scene: null,
        reason: null,
        attempt: state.attempt + 1,
      };

    case "hello": {
      // A late hello after we've already given up (offline) still recovers the
      // scene — the guest came alive. Ignore a duplicate hello once live.
      if (state.phase === "live") return state;
      if (!isProtocolCompatible(event.capabilities.protocolVersion)) {
        return {
          ...state,
          phase: "offline",
          reason: `scene speaks ${event.capabilities.protocolVersion}, host speaks ${SURFACE_PROTOCOL_VERSION}`,
        };
      }
      return { ...state, phase: "live", capabilities: event.capabilities, scene: event.scene, reason: null };
    }

    case "timeout":
      // Only the connecting phase degrades on timeout; a live scene stays live.
      if (state.phase !== "connecting") return state;
      return { ...state, phase: "offline", reason: "no response from the scene server" };

    case "error":
      if (state.phase === "live") return state;
      return { ...state, phase: "offline", reason: event.reason };

    default: {
      // Exhaustiveness: a new event type must be handled above.
      const _never: never = event;
      return _never;
    }
  }
}
